import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import es from "date-fns/locale/es";
import { esAdmin as esAdminCuenta } from "./roles";
import PrecioAdmin, { precioFinal } from "./PrecioAdmin";
import { etiquetaEstado } from "./pagos";
import Swal from "sweetalert2";
import { firestore } from "./firebase";
import { enviarMail } from "./apiEmail";
import "./Reservas.css"; // reutilizamos estilos del bloque-resumen

// --- FIX FECHA: parsear "YYYY-MM-DD" como fecha local (no UTC) ---
function parseFechaLocal(yyyy_mm_dd) {
  if (!yyyy_mm_dd) return null;
  const [y, m, d] = yyyy_mm_dd.split("-").map(Number);
  // Mediodía local evita problemas raros de DST
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

function Dia({ usuario }) {
  const [fecha, setFecha] = useState("");
  const [personas, setPersonas] = useState(1);

  // admin?
  const esAdmin = esAdminCuenta(usuario);

  // campos de cliente (solo admin)
  const [clienteNombre, setClienteNombre] = useState("");
  const [clienteApellido, setClienteApellido] = useState("");
  // Precio especial (solo admin): normal | acordado | sinCosto
  const [precioTipo, setPrecioTipo] = useState("normal");
  const [precioAcordado, setPrecioAcordado] = useState("");

  // precio dinámico
  const [precioDia, setPrecioDia] = useState(145);
  useEffect(() => {
    const unsub = firestore
      .collection("config")
      .doc("precios_extra")
      .onSnapshot((doc) => {
        const d = doc.data() || {};
        setPrecioDia(Number(d.diaPorPersona ?? 145));
      });
    return () => unsub && unsub();
  }, []);

  // registros del usuario (o del admin: los que creó el admin)
  const [registros, setRegistros] = useState([]);
  useEffect(() => {
    if (!usuario?.uid) return;
    const unsub = firestore
      .collection("registros")
      .where("creadoPorUid", "==", usuario.uid)
      .onSnapshot((snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRegistros(
          arr
            .filter((r) => r.tipo === "dia")
            .sort(
              (a, b) =>
                (b.creadoEn?.toMillis?.() ?? 0) -
                (a.creadoEn?.toMillis?.() ?? 0)
            )
        );
      });
    return () => unsub && unsub();
  }, [usuario?.uid]);

  // cancelaciones del usuario
  const [cancelaciones, setCancelaciones] = useState([]);
  useEffect(() => {
    if (!usuario?.email) return;
    const unsub = firestore
      .collection("cancelaciones")
      .where("usuarioEmail", "==", usuario.email)
      .onSnapshot((snap) => {
        const data = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          creadoEn: d.data().creadoEn?.toDate?.() ?? null,
        }));
        setCancelaciones(data);
      });
    return () => unsub && unsub();
  }, [usuario?.email]);

  const cancelByRegistroId = useMemo(() => {
    const out = {};
    for (const c of cancelaciones) {
      if (c.registroId) {
        const t = c.creadoEn?.getTime?.() || 0;
        const pt = out[c.registroId]?.creadoEn?.getTime?.() || 0;
        if (!out[c.registroId] || t >= pt) out[c.registroId] = c;
      }
    }
    return out;
  }, [cancelaciones]);

  const totalNormal = useMemo(() => {
    const p = Number(personas) || 0;
    return p > 0 && fecha ? p * Number(precioDia) : 0;
  }, [personas, fecha, precioDia]);

  const total = esAdmin ? precioFinal(precioTipo, precioAcordado, totalNormal) : totalNormal;

  // --- mails de confirmación ---
  async function enviarMailRegistroDia({ nombre, email, fecha, personas, total }) {
    const f = `${format(fecha, "dd/MM/yyyy", { locale: es })}`;
    const totalUYU = Number(total || 0).toLocaleString("es-UY");

    try {
      // Usuario
      await enviarMail({
        to: email,
        subject: `Registro por el Día confirmado · ${f}`,
        html: `
          <p>Hola ${nombre},</p>
          <p>Tu registro por el <b>Día</b> fue recibido:</p>
          <ul>
            <li>Fecha: ${f}</li>
            <li>Personas: ${personas}</li>
            <li>Total estimado: <b>UYU ${totalUYU}</b></li>
          </ul>
          <p>💳 Estado de pago: <b>pendiente</b> (el admin lo confirmará).</p>
          <p><a href="https://www.reservascuchi.com/info#camping">Información útil</a></p>
          <p>📲 Enviar comprobante de pago a <b>Ricardo Bachmann</b> al: <a href="https://wa.me/59899503445">099 503 445</a></p>
        `,
        text:
          `Registro Día\n` +
          `Fecha: ${f}\n` +
          `Personas: ${personas}\n` +
          `Total estimado: UYU ${totalUYU}\n` +
          `Info: https://www.reservascuchi.com/info#camping\n` +
          `Enviar comprobante de pago a Ricardo Bachmann al: 099 503 445\n`,
      });

      // Copia admin
      await enviarMail({
        to: "reservas.cuchillaalta@gmail.com",
        subject: `Nuevo registro Día · ${nombre} · ${f}`,
        html: `
          <p>Nuevo registro por el Día:</p>
          <ul>
            <li>Cliente: ${nombre} (${email})</li>
            <li>Fecha: ${f}</li>
            <li>Personas: ${personas}</li>
            <li>Total estimado: <b>UYU ${totalUYU}</b></li>
          </ul>
        `,
        text:
          `Nuevo registro Día · ${nombre} (${email}) · ${f}\n` +
          `${personas} pers · Total UYU ${totalUYU}\n`,
      });
    } catch (e) {
      console.error("Error al enviar correos de día:", e);
    }
  }

  const registrar = async () => {
    if (!usuario?.email)
      return Swal.fire("Error", "Debés iniciar sesión.", "error");
    if (!fecha) return Swal.fire("Error", "Elegí una fecha.", "error");
    if ((Number(personas) || 0) < 1)
      return Swal.fire("Error", "Indicá la cantidad de personas.", "error");
    if (esAdmin && precioTipo === "acordado" && precioAcordado === "")
      return Swal.fire("Error", "Ingresá el monto acordado.", "error");

    const nombreCliente = esAdmin
      ? `${clienteNombre} ${clienteApellido}`.trim() || usuario.email
      : `${usuario?.nombre || ""} ${usuario?.apellido || ""}`.trim() ||
        usuario.email;

    // --- FIX FECHA: guardar como Date local, no new Date("YYYY-MM-DD") ---
    const fechaDate = parseFechaLocal(fecha);

    try {
      await firestore.collection("registros").add({
        tipo: "dia",
        email: usuario.email,
        creadoPorUid: usuario.uid,
        creadoPorEmail: usuario.email,
        origen: esAdmin ? "admin-dia" : "web-usuario",
        nombre: nombreCliente,
        fecha: fechaDate,
        personas: Number(personas),
        precioUnit: Number(precioDia),
        total: Number(total),
        precioTipo: esAdmin ? precioTipo : "normal",
        paymentStatus: esAdmin && precioTipo === "sinCosto" ? "sinCosto" : "pendiente",
        cancelRequested: false,
        cancelRequestId: "",
        creadoEn: new Date(),
      });

      await enviarMailRegistroDia({
        nombre: nombreCliente,
        email: usuario.email,
        fecha: fechaDate,
        personas: Number(personas),
        total: Number(total),
      });

      Swal.fire("Listo", "Registro de día guardado.", "success");
      setFecha("");
      setPersonas(1);
      setClienteNombre("");
      setClienteApellido("");
      setPrecioTipo("normal");
      setPrecioAcordado("");
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "No se pudo guardar.", "error");
    }
  };

  // eliminar directo (solo admin)
  const eliminarRegistro = async (id) => {
    const res = await Swal.fire({
      title: "¿Eliminar registro?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "No",
    });
    if (!res.isConfirmed) return;
    try {
      await firestore.collection("registros").doc(id).delete();
      Swal.fire("Eliminado", "Registro eliminado.", "info");
    } catch {
      Swal.fire("Error", "No se pudo eliminar.", "error");
    }
  };

  const solicitarEliminacion = async (reg) => {
    const res = await Swal.fire({
      title: "Solicitar eliminación",
      text: "El administrador revisará tu solicitud antes de eliminar el registro.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Enviar solicitud",
      cancelButtonText: "Cancelar",
    });
    if (!res.isConfirmed) return;

    try {
      const docRef = await firestore.collection("cancelaciones").add({
        tipo: "registro",
        registroId: reg.id,
        registroTipo: "dia",
        usuarioEmail: usuario.email,
        usuarioNombre: usuario?.nombre || "",
        usuarioApellido: usuario?.apellido || "",
        motivo: "usuario-solicita-eliminar",
        estado: "pendiente",
        creadoEn: new Date(),
      });

      await firestore.collection("registros").doc(reg.id).update({
        cancelRequested: true,
        cancelRequestId: docRef.id,
      });

      try {
        await enviarMail({
          to: "reservas.cuchillaalta@gmail.com",
          subject: `Solicitud de eliminación · Día · ${usuario.email}`,
          html: `<p>Registro ID: ${reg.id}</p><p>Usuario: ${usuario.email}</p>`,
          text: `Registro ID: ${reg.id}\nUsuario: ${usuario.email}\n`,
        });
      } catch {}

      Swal.fire(
        "Enviado",
        "Solicitud registrada. Te avisaremos cuando se resuelva.",
        "success"
      );
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "No se pudo enviar la solicitud.", "error");
    }
  };

  return (
    <div className="container">
      <h1>Registrar estadía por el Día</h1>

      <div className="formulario">
        {esAdmin && (
          <>
            <div className="form-group">
              <label>Nombre Cliente:</label>
              <input
                value={clienteNombre}
                onChange={(e) => setClienteNombre(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Apellido Cliente:</label>
              <input
                value={clienteApellido}
                onChange={(e) => setClienteApellido(e.target.value)}
              />
            </div>
          </>
        )}

        <div className="form-group">
          <label>Fecha:</label>
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Personas:</label>
          <input
            type="number"
            min="1"
            value={personas}
            onChange={(e) => setPersonas(e.target.value)}
            style={{ width: 120 }}
          />
        </div>

        <div style={{ fontSize: 12, color: "#555", margin: "6px 0 10px" }}>
          👶 Los niños menores de 12 años <b>no pagan</b> ni <b>se registran</b>.
        </div>

        <div className="form-group" style={{ marginTop: 8 }}>
          Precio por persona/día:{" "}
          <b>UYU {Number(precioDia).toLocaleString("es-UY")}</b>
        </div>

        {esAdmin && Number(personas) > 0 && (
          <PrecioAdmin
            tipo={precioTipo}
            setTipo={setPrecioTipo}
            monto={precioAcordado}
            setMonto={setPrecioAcordado}
            normal={totalNormal}
          />
        )}

        {fecha && Number(personas) > 0 && (
          <div className="bloque-resumen-confirmar">
            <div className="resumen-reserva">
              <span style={{ whiteSpace: "nowrap" }}>
                👥 {Number(personas)} pers
              </span>
              <span style={{ whiteSpace: "nowrap" }}>
                💰 {esAdmin && precioTipo === "sinCosto"
                  ? "Sin costo"
                  : `UYU ${Number(total).toLocaleString("es-UY")}`}
              </span>
            </div>
            <button className="confirmar-btn" onClick={registrar}>
              Registrar
            </button>
          </div>
        )}
      </div>

      <div className="seccion-mis-reservas" style={{ marginTop: 28 }}>
        <h2>
          {esAdmin
            ? "Registros hechos por el Administrador"
            : "Mis registros por el día"}
        </h2>
        <ul>
          {registros.map((r) => {
            const csol = cancelByRegistroId[r.id];
            const estadoCancel = csol?.estado;

            return (
              <li key={r.id} className="reserva-item">
                <span className="reserva-text">
                  {r.nombre || r.email} · {r.personas} pers ·{" "}
                  {format(
                    r.fecha.toDate ? r.fecha.toDate() : r.fecha,
                    "dd/MM/yyyy",
                    { locale: es }
                  )}{" "}
                  · <b>UYU {Number(r.total).toLocaleString("es-UY")}</b>
                </span>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  {(() => {
                    const st = ["parcial", "confirmado", "sinCosto"].includes(r.paymentStatus)
                      ? r.paymentStatus
                      : "pendiente";
                    return <span className={`badge badge--${st}`}>{etiquetaEstado(st)}</span>;
                  })()}

                  {estadoCancel === "pendiente" && (
                    <span className="badge badge--c-pendiente">
                      Eliminación pendiente
                    </span>
                  )}
                  {estadoCancel === "rechazada" && (
                    <span className="badge badge--c-rechazada">
                      Eliminación rechazada
                    </span>
                  )}

                  {esAdmin ? (
                    <button
                      className="cancelar-btn"
                      onClick={() => eliminarRegistro(r.id)}
                    >
                      Eliminar registro
                    </button>
                  ) : (
                    <button
                      className="cancelar-btn"
                      onClick={() => solicitarEliminacion(r)}
                      disabled={estadoCancel === "pendiente"}
                      title={
                        estadoCancel === "pendiente"
                          ? "Ya hay una solicitud pendiente"
                          : ""
                      }
                    >
                      Solicitar eliminación
                    </button>
                  )}
                </div>
              </li>
            );
          })}
          {registros.length === 0 && <p>No tenés registros aún.</p>}
        </ul>
      </div>
    </div>
  );
}

export default Dia;